// ═══════════════════════════════════════════════════════════════════════════════
// SESSION RECORDINGS PANEL
// Displays a gallery of recorded Chime video sessions with play/download/transcript
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';
import { X, Play, Download, FileText, Loader2, Video, Mic, Clock, Calendar, Search, Trash2, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface SessionRecording {
  id: number;
  meeting_id: string;
  user_id: string;
  session_title: string;
  client_name: string;
  media_pipeline_id: string | null;
  recording_url: string | null;
  duration_seconds: number | null;
  status: string;
  transcript_text: string | null;
  transcript_status: string;
  consent_override: boolean;
  consent_override_reason: string | null;
  consent_override_practitioner: string | null;
  consent_overridden_at: string | null;
  created_at: string;
  updated_at: string;
}

interface SessionRecordingsPanelProps {
  onClose?: () => void;
  embedded?: boolean;
  onMinimize?: (minimized: boolean) => void;
}

export default function SessionRecordingsPanel({ onClose, embedded = false, onMinimize }: SessionRecordingsPanelProps) {
  const { user } = useAuth();
  const [recordings, setRecordings] = useState<SessionRecording[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Load recordings on mount
  useEffect(() => {
    loadRecordings();
  }, [user?.id]);

  const loadRecordings = async () => {
    if (!user?.id) return;
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('whipt_session_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const resp = await fetch(`/api/video/recordings?user_id=${user.id}`, { headers });
      const data = await resp.json();

      if (data.recordings) {
        setRecordings(data.recordings);
      } else {
        setRecordings([]);
      }
    } catch (err: any) {
      console.warn('Failed to load recordings:', err);
      setError(err.message || 'Failed to load recordings');
      setRecordings([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter recordings by search
  const filteredRecordings = useMemo(() => {
    if (!searchQuery.trim()) return recordings;
    const q = searchQuery.toLowerCase();
    return recordings.filter(r =>
      r.session_title?.toLowerCase().includes(q) ||
      r.client_name?.toLowerCase().includes(q) ||
      r.meeting_id?.toLowerCase().includes(q)
    );
  }, [recordings, searchQuery]);

  // Format duration
  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // Format date
  const formatDate = (dateStr: string): string => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  // Download transcript as text file
  const downloadTranscript = (recording: SessionRecording) => {
    if (!recording.transcript_text) return;
    const blob = new Blob([recording.transcript_text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${recording.session_title || 'session'}-transcript.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Delete recording
  const deleteRecording = async (recordingId: number) => {
    if (!confirm('Delete this recording? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('whipt_session_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const resp = await fetch(`/api/video/recordings/${recordingId}`, {
        method: 'DELETE', headers
      });
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to delete');
      }
      setRecordings(prev => prev.filter(r => r.id !== recordingId));
    } catch (err) {
      console.warn('Failed to delete recording:', err);
    }
  };

  const handleMinimize = () => {
    const next = !isMinimized;
    setIsMinimized(next);
    onMinimize?.(next);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const panelContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg">
            <Video className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Session Recordings</h2>
            <p className="text-xs text-white/60">{recordings.length} recording{recordings.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleMinimize} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            {isMinimized ? <ChevronUp className="w-4 h-4 text-white/70" /> : <ChevronDown className="w-4 h-4 text-white/70" />}
          </button>
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-4 h-4 text-white/70" />
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search recordings..."
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-rose-400/40"
          />
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-rose-400 animate-spin mx-auto mb-3" />
            <p className="text-sm text-white/50">Loading recordings...</p>
          </div>
        </div>
      )}

      {/* Error state */}
      {!isLoading && error && (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <p className="text-sm text-rose-300 mb-2">{error}</p>
            <button onClick={loadRecordings} className="text-xs text-rose-400 underline hover:text-rose-300">
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && filteredRecordings.length === 0 && (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500/20 to-pink-600/20 flex items-center justify-center mx-auto mb-4 border border-rose-400/20">
              <Video className="w-8 h-8 text-rose-400/50" />
            </div>
            <h3 className="text-base font-semibold text-white mb-2">No recordings yet</h3>
            <p className="text-sm text-white/50 max-w-xs">
              {searchQuery ? 'No recordings match your search.' : 'Start a video session and record it — recordings will appear here.'}
            </p>
          </div>
        </div>
      )}

      {/* Recordings list */}
      {!isLoading && !error && filteredRecordings.length > 0 && (
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
          {filteredRecordings.map(recording => (
            <div
              key={recording.id}
              className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] overflow-hidden hover:border-rose-400/30 transition-all"
            >
              {/* Header row */}
              <button
                onClick={() => setExpandedId(expandedId === recording.id ? null : recording.id)}
                className="w-full flex items-center gap-4 p-4 text-left"
              >
                {/* Icon */}
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500/20 to-pink-600/20 flex items-center justify-center flex-shrink-0 border border-rose-400/20">
                  {recording.media_pipeline_id ? (
                    <Video className="w-5 h-5 text-rose-400" />
                  ) : (
                    <Mic className="w-5 h-5 text-rose-400" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">
                    {recording.session_title || 'Untitled Session'}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="flex items-center gap-1 text-xs text-white/40">
                      <Calendar className="w-3 h-3" />
                      {formatDate(recording.created_at)}
                    </span>
                    {recording.duration_seconds ? (
                      <span className="flex items-center gap-1 text-xs text-white/40">
                        <Clock className="w-3 h-3" />
                        {formatDuration(recording.duration_seconds)}
                      </span>
                    ) : null}
                    {recording.transcript_status === 'completed' && (
                      <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold">
                        Transcript Ready
                      </span>
                    )}
                  </div>
                </div>

                {/* Expand toggle */}
                {expandedId === recording.id ? (
                  <ChevronUp className="w-4 h-4 text-white/40 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0" />
                )}
              </button>

              {/* Expanded details */}
              {expandedId === recording.id && (
                <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2">
                    {recording.recording_url && (
                      <a
                        href={recording.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500/20 border border-rose-400/30 text-rose-200 text-xs font-semibold hover:bg-rose-500/30 transition-all"
                      >
                        <Play className="w-3.5 h-3.5" />
                        Play
                      </a>
                    )}
                    {recording.recording_url && (
                      <a
                        href={recording.recording_url}
                        download
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-400/30 text-blue-200 text-xs font-semibold hover:bg-blue-500/30 transition-all"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download
                      </a>
                    )}
                    {recording.transcript_text && (
                      <button
                        onClick={() => downloadTranscript(recording)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 text-xs font-semibold hover:bg-emerald-500/30 transition-all"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Transcript
                      </button>
                    )}
                    <button
                      onClick={() => deleteRecording(recording.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-xs font-semibold hover:bg-red-500/30 transition-all ml-auto"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>

                  {/* Metadata */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-white/40">Client:</span>{' '}
                      <span className="text-white/70">{recording.client_name || 'Participant'}</span>
                    </div>
                    <div>
                      <span className="text-white/40">Status:</span>{' '}
                      <span className="text-white/70 capitalize">{recording.status}</span>
                    </div>
                    <div>
                      <span className="text-white/40">Transcript:</span>{' '}
                      <span className="text-white/70 capitalize">{recording.transcript_status || 'pending'}</span>
                    </div>
                    <div>
                      <span className="text-white/40">Meeting ID:</span>{' '}
                      <span className="text-white/50 font-mono text-[10px]">{recording.meeting_id?.slice(0, 16)}...</span>
                    </div>
                  </div>

                  {/* Transcript preview */}
                  {recording.transcript_text && (
                    <div className="mt-2 p-3 rounded-xl bg-white/5 border border-white/5 max-h-32 overflow-y-auto">
                      <p className="text-xs text-white/50 leading-relaxed whitespace-pre-wrap line-clamp-5">
                        {recording.transcript_text.slice(0, 500)}
                        {recording.transcript_text.length > 500 ? '...' : ''}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Wrapper ─────────────────────────────────────────────────────────────────
  if (embedded) {
    return (
      <div className="w-full h-full rounded-2xl border-2 border-rose-400/30 bg-gradient-to-br from-rose-950/40 to-slate-900/60 backdrop-blur-xl overflow-hidden shadow-2xl">
        {panelContent}
      </div>
    );
  }

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 z-[10000] px-4 py-3 rounded-2xl bg-gradient-to-br from-rose-600 to-pink-700 text-white font-bold shadow-2xl border border-rose-400/50 hover:brightness-110 transition-all flex items-center gap-3"
      >
        <Video className="w-5 h-5" />
        <span>Recordings ({recordings.length})</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] rounded-3xl border-2 border-rose-400/40 bg-gradient-to-br from-rose-950/60 to-slate-900/80 backdrop-blur-2xl overflow-hidden shadow-[0_20px_60px_rgba(244,63,94,0.25)]">
        {panelContent}
      </div>
    </div>
  );
}